import React from 'react';

import { AlertBox, AlertBoxDismissButton, Box } from '@vp/swan';
import { useLocalizations } from '@vp/ubik-localizations-provider';

import { useBrandkitFailed } from '../hooks/useBrandkitFailed';
import { useCurrentDocumentInvalidated } from '../hooks/useCurrentDocumentInvalidated';
import { useItemAddedToCart } from '../hooks/useItemAddedToCart';
import { useSelectionValidated } from '../hooks/useSelectionValidated';

const alertBoxSelectorClassname = 'spd-alert-box-selector';

export interface ToastInfo {
  skin?: 'standard' | 'error' | 'warning' | 'positive' | 'legal-warning';
  body?: React.ReactNode;
  id?: string;
}

export type ToastProvider = (
  setToast: React.Dispatch<React.SetStateAction<ToastInfo | undefined>>,
  unsetToast: (toastId: string) => void
) => void;

export const StickyToast: React.FC = () => {
  const [toast, setToast] = React.useState<ToastInfo | undefined>(undefined);
  const { t } = useLocalizations();

  const toastProviders: ToastProvider[] = [
    useBrandkitFailed,
    useCurrentDocumentInvalidated,
    useItemAddedToCart,
    useSelectionValidated,
  ];

  const unsetToast = (toastId: string): void => {
    setToast((prevToast) => (prevToast?.id === toastId ? undefined : prevToast));
  };

  toastProviders.forEach((provider) => provider(setToast, unsetToast));

  return (
    toast && (
      <AlertBox
        skin={toast.skin}
        toast={true}
        aria-live="polite"
        onRequestDismiss={() => {
          setToast(undefined);
        }}
        className={alertBoxSelectorClassname}
        accessibleTextForIcon={t(toast.skin || 'standard')}
      >
        <Box paddingLeft={'4'} paddingRight={7}>
          {toast.body}
        </Box>
        <AlertBoxDismissButton accessibleText={t('close')} />
      </AlertBox>
    )
  );
};
